// Per-kid learning summary for the family drill-in.
//
// 🔴 READ-ONLY OVER A FROZEN ENGINE. Everything on this card comes from GET
// /admin/progress/family/:id, which does nothing but SELECT. There is no edit
// affordance here and none may be added: scoring, the XP ledger and streaks are
// owned by the learning engine alone. A staff member looks; they do not adjust.
//
// 🔴 CHILDREN'S PRIVACY (CLAUDE.md §4). This is a minor's record. It shows
// counts, timestamps and lesson identifiers — never the child's actual answers
// or recordings, which the API deliberately does not return. The API also
// audit-logs every read, so "who looked at this child's data" has an answer.
//
// ⚠️ `averageScore: null` means NOTHING HAS BEEN SCORED YET, which is a
// different fact from an average of zero. It renders as an em dash, never 0.

import { StatTile, StatGrid } from '../StatTile';
import { StatusBadge } from '../StatusBadge';

import type { KidProgress } from '~/lib/admin';

export function KidProgressCard({ kid }: { kid: KidProgress }) {
  const hasActivity =
    kid.lessonsCompleted > 0 ||
    kid.lessonsInProgress > 0 ||
    kid.xpTotal > 0 ||
    kid.recentAttempts.length > 0;

  return (
    <section className="rounded-2xl border border-admin-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-admin-900">{kid.label}</h3>
        {kid.username && <span className="text-sm text-admin-400">@{kid.username}</span>}
        <StatusBadge status={kid.status} dot />
      </div>

      {!hasActivity ? (
        <p className="mt-3 text-sm text-admin-400">
          No learning activity yet — this learner has not started a lesson.
        </p>
      ) : (
        <>
          <StatGrid className="mt-4" columns={4}>
            <StatTile
              label="Lessons done"
              value={kid.lessonsCompleted}
              icon="book"
              hint={kid.lessonsInProgress > 0 ? `${kid.lessonsInProgress} in progress` : undefined}
            />
            <StatTile
              label="XP"
              value={kid.xpTotal.toLocaleString('en-US')}
              icon="sparkle"
              hint={
                kid.badges > 0 ? `${kid.badges} badge${kid.badges === 1 ? '' : 's'}` : undefined
              }
            />
            <StatTile
              label="Streak"
              value={`${kid.streakCurrent} day${kid.streakCurrent === 1 ? '' : 's'}`}
              icon="trendUp"
              hint={`longest ${kid.streakLongest}`}
            />
            <StatTile
              label="Average score"
              // null ⇒ nothing scored yet. Rendering 0 here would tell a parent
              // support call that their child is failing, which is not the fact.
              value={kid.averageScore === null ? '—' : `${kid.averageScore}%`}
              icon="chart"
              hint={kid.averageScore === null ? 'nothing scored yet' : undefined}
            />
          </StatGrid>

          <dl className="mt-4 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <Row label="Time spent">{formatDuration(kid.totalTimeSpentMs)}</Row>
            <Row label="Tracks enrolled">{kid.enrollments}</Row>
            <Row label="Last lesson">
              {kid.lastLessonAt ? formatDateTime(kid.lastLessonAt) : '—'}
            </Row>
            <Row label="Last active">
              {/* A `date` column — already 'YYYY-MM-DD', so it is NOT parsed as
                  a timestamp (which would shift it by the viewer's offset). */}
              {kid.lastActiveDate ?? '—'}
            </Row>
          </dl>

          {kid.recentAttempts.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-medium uppercase tracking-wide text-admin-400">
                Recent attempts
              </p>
              <ul className="mt-2 divide-y divide-admin-100">
                {kid.recentAttempts.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-sm text-admin-700">
                      {/* A translation KEY, shown raw: the admin has no i18n
                          runtime (English-only chrome), and the key is a stable
                          identifier a content editor can search for. */}
                      {a.lessonTitleKey ?? a.activityKind ?? 'activity'}
                    </span>
                    {a.activityKind && (
                      <span className="text-xs text-admin-400">{a.activityKind}</span>
                    )}
                    {/* Tri-state: null means this activity type is not graded,
                        which must not read as "wrong". */}
                    {a.correct !== null && (
                      <StatusBadge
                        status={a.correct ? 'correct' : 'incorrect'}
                        label={a.correct ? 'Correct' : 'Incorrect'}
                        tone={a.correct ? 'success' : 'warning'}
                      />
                    )}
                    {a.score !== null && (
                      <span className="text-xs tabular-nums text-admin-600">{a.score}%</span>
                    )}
                    <span className="text-xs text-admin-400">{formatDateTime(a.startedAt)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-xs uppercase tracking-wide text-admin-400">{label}</dt>
      <dd className="text-admin-700">{children}</dd>
    </div>
  );
}

/** Whole minutes/hours — a child's lesson time to the millisecond is noise. */
function formatDuration(ms: number): string {
  if (ms <= 0) return '—';
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
